package one.healthium.uscreen.ui.components

import androidx.compose.animation.AnimatedContent
import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.tween
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.togetherWith
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBars
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.delay
import one.healthium.ui.compose.components.AppShimmerConfig
import one.healthium.ui.compose.components.appShimmer
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.uscreen.domain.models.AnimationType
import one.healthium.uscreen.domain.models.UScreenCta
import one.healthium.uscreen.ui.animation.CTA_ANIM_DURATION
import one.healthium.uscreen.ui.animation.DEFAULT_CTA_IN_ANIM
import one.healthium.uscreen.ui.animation.DEFAULT_CTA_OUT_ANIM
import one.healthium.uscreen.ui.animation.LocalActiveScreenId
import one.healthium.uscreen.ui.animation.LocalNavigatingBack
import one.healthium.uscreen.ui.animation.TRANSITION_EASING
import one.healthium.uscreen.ui.util.clickableHapticOnPress
import one.healthium.uscreen.ui.util.parseHexColor

/**
 * CTA bar that lives inside the screen.
 * Supports both enter (inAnim) and exit (outAnim) via [LocalActiveScreenId].
 */
@Composable
fun UScreenCtaBarSimple(
    cta: UScreenCta,
    screenId: String = "",
    enabled: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val isBack = LocalNavigatingBack.current
    val activeScreenId = LocalActiveScreenId.current
    val isExiting = screenId.isNotEmpty() && activeScreenId.isNotEmpty() && screenId != activeScreenId

    val animType = when {
        isExiting && !isBack -> cta.transition?.outAnim ?: DEFAULT_CTA_OUT_ANIM  // push exit
        isExiting && isBack -> cta.transition?.inAnim ?: DEFAULT_CTA_IN_ANIM     // pop exit (reverse enter)
        !isExiting && !isBack -> cta.transition?.inAnim ?: DEFAULT_CTA_IN_ANIM   // push enter
        else -> cta.transition?.outAnim ?: DEFAULT_CTA_OUT_ANIM                  // pop enter (reverse exit)
    }

    val progress = remember(activeScreenId) { Animatable(if (isExiting) 1f else if (animType == AnimationType.None) 1f else 0f) }
    LaunchedEffect(activeScreenId) {
        if (isExiting) {
            progress.animateTo(0f, tween(CTA_ANIM_DURATION, easing = TRANSITION_EASING))
        } else if (progress.value < 1f) {
            val ctaDelay = (cta.transition?.delay ?: 0f)
            val totalDelay = (ctaDelay * 1000).toLong()
            delay(totalDelay)
            progress.animateTo(1f, tween(CTA_ANIM_DURATION, easing = TRANSITION_EASING))
        }
    }

    val p = progress.value
    val transitionModifier = when (animType) {
        AnimationType.Fade -> Modifier.graphicsLayer { alpha = p }
        AnimationType.SlideOut -> Modifier.graphicsLayer {
            val remaining = 1f - p
            alpha = p
            translationX = when {
                isExiting && isBack -> remaining * size.width
                isExiting -> -remaining * size.width
                isBack -> -remaining * size.width
                else -> remaining * size.width
            }
        }

        AnimationType.None -> Modifier
    }

    val bgAlpha by animateFloatAsState(
        targetValue = if (enabled) 1f else 0.4f,
        animationSpec = tween(200),
        label = "ctaEnabled",
    )

    Box(
        modifier = modifier
            .fillMaxWidth()
            .then(transitionModifier)
            .windowInsetsPadding(WindowInsets.navigationBars)
            .padding(horizontal = 24.dp, vertical = 16.dp)
            .testTag("cta_button"),
    ) {
        val shimmerConfig = if (cta.shimmer) {
            AppShimmerConfig(color = AppTheme.colors.windowBackground, duration = 1500, interval = 3000)
        } else {
            null
        }

        val shape = RoundedCornerShape(50)
        val gradientColors = resolveCtaGradientSimple(cta)
        val bgModifier = if (gradientColors != null) {
            Modifier.background(Brush.horizontalGradient(gradientColors.map { it.copy(alpha = bgAlpha) }), shape)
        } else {
            Modifier.background(AppTheme.colors.primary.copy(alpha = bgAlpha), shape)
        }

        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(60.dp)
                .pressScale()
                .appShimmer(shimmerConfig)
                .clip(shape)
                .then(bgModifier)
                .clickableHapticOnPress(enabled = enabled && !isExiting && p >= 1f, onClick = onClick),
            contentAlignment = Alignment.Center,
        ) {
            AnimatedContent(
                targetState = cta.text,
                transitionSpec = { fadeIn(tween(200)) togetherWith fadeOut(tween(150)) },
            ) { text ->
                Text(
                    text = text,
                    color = AppTheme.uscreenStyle.ctaContentColor,
                    fontSize = 17.sp,
                    fontWeight = FontWeight.SemiBold,
                )
            }
        }
    }
}

private fun resolveCtaGradientSimple(cta: UScreenCta): List<Color>? {
    val hexColors = cta.gradient ?: return null
    val colors = hexColors.mapNotNull { parseHexColor(it) }
    return colors.takeIf { it.size >= 2 }
}
